import React, { useEffect, useState, Fragment } from "react";
import { useLocation } from "react-router-dom";
import {
    Row,
    Col,
    Container,
    Card,
    CardBody,
    CardHeader,
    Spinner
} from "reactstrap";
import TrainingMaterialService from "./TrainingMaterial.service";
import "./TrainingMaterial.scss";

const TrainingMaterial = () => {
    const [loading, setLoading] = useState(true);
    const [docData, setDocData] = useState(null);
    const [error, setError] = useState(null);
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const docId = query.get("doc_id");
    const configDoc = query.get("config_doc");

    useEffect(() => {
        document.title = "Document | Edsha Solutions";
        if (configDoc) {
            fetchConfigDocument(configDoc);
        } else if (docId) {
            fetchDocument(docId);
        } else {
            setLoading(false);
            setError("No document ID provided.");
        }
    }, [docId, configDoc]);

    const fetchConfigDocument = (key) => {
        setLoading(true);
        TrainingMaterialService.getPublicConfigDocument(key)
            .then((resp) => {
                if (resp.data.status && resp.data.data.length > 0) {
                    const row = resp.data.data[0];
                    setDocData(row);
                    const typeName = row?.documentData?.[0]?.name || row?.documentData?.[0]?.pic_name || "";
                    document.title = `${typeName || "Document"} | Edsha Solutions`;
                } else {
                    setError("Document not found.");
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setError("An error occurred while fetching the document.");
                setLoading(false);
            });
    };

    const fetchDocument = (id) => {
        setLoading(true);
        TrainingMaterialService.getPublicDocument(id)
            .then((resp) => {
                if (resp.data.status && resp.data.data.length > 0) {
                    const row = resp.data.data[0];
                    setDocData(row);
                    const typeName = row?.documentData?.[0]?.name || row?.documentData?.[0]?.pic_name || "";
                    document.title = `${typeName || "Document"} | Edsha Solutions`;
                } else {
                    setError("Document not found.");
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setError("An error occurred while fetching the document.");
                setLoading(false);
            });
    };

    const docTypeLabel = docData?.documentData?.[0]?.name || docData?.documentData?.[0]?.pic_name || "";
    const heroSubtext = docTypeLabel
        ? `You are viewing a shared company document (${docTypeLabel}). Review or download the file in the section below.`
        : "You are viewing a shared company document. Review or download the file in the section below.";
    const mediaTitle = docTypeLabel || "Document";
    const reviewLead = docTypeLabel
        ? `Please review the "${docTypeLabel}" file carefully. If anything is unclear, contact your account representative.`
        : "Please review this file carefully. If anything is unclear, contact your account representative.";

    const renderFile = () => {
        if (!docData || !docData.file) return <p>No file associated with this document.</p>;

        let fileUrl = "";
        let fileExtension = "";
        const isBase64 = docData.file.startsWith("data:");

        if (isBase64) {
            fileUrl = docData.file;
            // Extract extension from mime type: data:application/pdf;base64,...
            const mimeType = docData.file.split(';')[0].split(':')[1];
            fileExtension = mimeType.split('/')[1]; // e.g., pdf, png, jpeg
        } else {
            fileUrl = `${process.env.REACT_APP_LOGINURL.replace('/api/', '')}/fileuploads/${docData.file}`;
            fileExtension = docData.file.split('.').pop().toLowerCase();
        }

        if (fileExtension === "pdf") {
            return (
                <div className="pdf-container">
                    <iframe
                        src={`${fileUrl}${isBase64 ? "" : "#toolbar=0"}`}
                        title={mediaTitle}
                        width="100%"
                        height="800px"
                        frameBorder="0"
                    ></iframe>
                </div>
            );
        } else if (["jpg", "jpeg", "png", "gif", "webp"].includes(fileExtension)) {
            return (
                <div className="image-container text-center">
                    <img src={fileUrl} alt={mediaTitle} className="img-fluid rounded shadow-lg" />
                </div>
            );
        } else {
            return (
                <div className="download-container text-center py-5">
                    <i className="pe-7s-download display-1 text-primary mb-3"></i>
                    <h4>{isBase64 ? "Document Preview" : "Unsupported file format for preview"}</h4>
                    <p>{isBase64 ? "Click below to view/download the full document." : "Please download the file to view it."}</p>
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg mt-3" download={isBase64 ? `document.${fileExtension}` : docData.file}>
                        Download Document
                    </a>
                </div>
            );
        }
    };

    return (
        <Fragment>
            <div className="training-material-page">
                <div className="hero-section">
                    <Container>
                        <div className="hero-content text-center text-white py-5 px-2">
                            <p className="mb-0 fw-bold" style={{ fontSize: 'clamp(0.625rem, 1.4vw, 0.725rem)', lineHeight: 1.45, maxWidth: '52rem', margin: '0 auto' }}>
                                {error
                                    ? 'Document could not be loaded.'
                                    : loading && docId && !docData
                                        ? 'Loading document…'
                                        : heroSubtext}
                            </p>
                        </div>
                    </Container>
                </div>

                <Container className="mt-n5">
                    <Row className="justify-content-center">
                        <Col lg="10">
                            <Card className="main-card mb-5 shadow-lg border-0">
                                <CardHeader className="bg-white border-bottom-0 pt-4 px-4">
                                    <div className="d-flex align-items-center justify-content-between w-100">
                                            {/* <h3 className="card-title text-primary fw-bold mb-0">
                                                {docData?.documentData?.[0]?.name || "Document View"}
                                            </h3> */}
                                        <div className="badge bg-soft-primary text-primary p-2 px-3">
                                            {docData?.documentData?.[0]?.name || "Official"}
                                        </div>
                                        <div>
                                            {docData?.created_at && (
                                                <small className="text-muted">
                                                    Published on: {new Date(docData.created_at).toLocaleDateString()}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody style={{padding:"0.5rem 1.5rem 1.5rem 1.5rem"}}>
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                                            <p className="mt-3 text-muted">Loading document, please wait...</p>
                                        </div>
                                    ) : error ? (
                                        <div className="text-center py-5">
                                            <div className="error-icon mb-4">
                                                <i className="pe-7s-attention display-1 text-danger"></i>
                                            </div>
                                            <h2 className="text-dark">Oops!</h2>
                                            <p className="text-muted lead">{error}</p>
                                        </div>
                                    ) : (
                                        <div className="document-content">
                                            <div className="mb-1">
                                                <p className="lead text-dark" style={{ fontSize: '13px' }}>
                                                    {reviewLead}
                                                </p>
                                            </div>
                                            <hr className="my-1 mb-2 opacity-1" />
                                            {renderFile()}

                                            {/* <div className="mt-5 p-4 bg-light rounded-3">
                                                <h5 className="fw-bold mb-3"><i className="pe-7s-info me-2 text-primary"></i>Important Note:</h5>
                                                <p className="mb-0 text-muted">
                                                    If you have any questions regarding these materials or regulations, please reach out to your account manager
                                                    or contact our support team at <a href="mailto:support@edshasolutions.com">support@edshasolutions.com</a>.
                                                </p>
                                            </div> */}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>

                <footer className="footer py-4 text-center text-muted border-top bg-white">
                    <Container>
                        <p className="mb-0">© {new Date().getFullYear()} Edsha Solutions. All Rights Reserved.</p>
                    </Container>
                </footer>
            </div>
        </Fragment>
    );
};

export default TrainingMaterial;
